import React, { useEffect, useState } from 'react';
import { FaFacebookF, FaGoogle, FaEye, FaEyeSlash, FaUserShield, FaBolt, FaChartLine } from 'react-icons/fa';
import axios from 'axios';
import Cookies from 'js-cookie';
import logo from '../../assets/miles_logo.svg';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const AdminSignin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const token = Cookies.get('access_token');
        if (token) {
            navigate('/dashboard');
        }

        // Auto-rotate slides
        const interval = setInterval(() => {
            setActiveSlide(prev => (prev + 1) % sliderData.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [navigate]);

    const sliderData = [
        {
            icon: <FaUserShield size={48} className="text-secondary" />,
            title: "Trusted Land Ownership",
            description: "Secure land transactions backed by government-verified documentation.",
            subDescription: "Your investment is safe with Milescoop's rigorous property verification process."
        },
        {
            icon: <FaBolt size={48} className="text-secondary" />,
            title: "Swift & Transparent Process",
            description: "Enjoy hassle-free, transparent land transactions.",
            subDescription: "Our streamlined process ensures smooth, reliable transfers without unnecessary delays."
        },
        {
            icon: <FaChartLine size={48} className="text-secondary" />,
            title: "Investment Growth Insights",
            description: "Make informed choices with data-driven insights on Kenya's land market.",
            subDescription: "Milescoop provides valuable analytics to help you choose high-potential properties."
        }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post('https://api.milescoop.com/api/v1/auth/user-signin', {
                email,
                password,
            }, {
                headers: {
                    'api-key': '14CA4D6A5CE4D441CFBFB23CCB699',
                },
            });
            const { token, user } = response.data;
            const userId = user.id;

            const expiration = rememberMe ? 30 : 1;
            Cookies.set('access_token', token, { expires: expiration });
            Cookies.set('user_id', userId, { expires: expiration });
            localStorage.setItem('user', JSON.stringify(user));

            navigate('/dashboard');
            window.location.reload();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || 'Request failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gradient-to-br from-[#CFD4D8] to-[#e8ecf0] min-h-screen w-full flex items-center justify-center px-4 py-8"
        >
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col md:flex-row mx-auto bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl overflow-hidden w-full max-w-6xl"
            >
                {/* Form Section */}
                <div className="w-full md:w-1/2 p-8 md:p-12">
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="mb-8 flex justify-center"
                    >
                        <img src={logo} className="h-14" alt="Miles Logo" />
                    </motion.div>

                    <motion.form
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-6"
                    >
                        <div className="text-center mb-4">
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h1>
                            <p className="text-gray-500">Sign in to your account</p>
                        </div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-red-50 text-red-600 p-3 rounded-xl text-sm"
                            >
                                {error}
                            </motion.div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="text-gray-700 text-sm font-medium mb-1 block">Email</label>
                                <motion.div
                                    whileFocus={{ scale: 1.01 }}
                                    className="relative"
                                >
                                    <input
                                        type="email"
                                        placeholder="Enter your email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={loading}
                                        className="w-full bg-gray-50/80 rounded-xl py-3 px-4 border border-gray-200 text-gray-800 outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition-all duration-200"
                                    />
                                </motion.div>
                            </div>

                            <div>
                                <label htmlFor="password" className="text-gray-700 text-sm font-medium mb-1 block">Password</label>
                                <motion.div
                                    whileFocus={{ scale: 1.01 }}
                                    className="relative"
                                >
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your password"
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={loading}
                                        className="w-full bg-gray-50/80 rounded-xl py-3 px-4 border border-gray-200 text-gray-800 outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition-all duration-200"
                                    />
                                    <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-700"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </motion.button>
                                </motion.div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <motion.div
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    className="relative"
                                >
                                    <input
                                        type="checkbox"
                                        id="remember"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="sr-only"
                                    />
                                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${rememberMe ? 'bg-secondary border-secondary' : 'border-gray-300'}`}>
                                        {rememberMe && (
                                            <motion.svg
                                                initial={{ opacity: 0, scale: 0.5 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                className="w-3 h-3 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </motion.svg>
                                        )}
                                    </div>
                                </motion.div>
                                <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">Remember for 30 days</label>
                            </div>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                type="button"
                                className="text-secondary text-sm font-medium hover:underline"
                            >
                                Forgot password?
                            </motion.button>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={loading}
                            type="submit"
                            className="bg-secondary mt-4 w-full text-white font-medium py-3.5 rounded-xl shadow-lg shadow-secondary/20 hover:shadow-secondary/30 transition-all duration-300"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                                    />
                                    Signing In...
                                </div>
                            ) : 'Sign In'}
                        </motion.button>
                    </motion.form>
                </div>

                {/* Slider Section */}
                <div className="hidden md:block w-1/2 bg-gradient-to-bl from-secondary/10 to-secondary/5 backdrop-blur-md p-12 relative overflow-hidden">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.2 }}
                        className="absolute inset-0 z-0"
                        style={{
                            backgroundImage: 'radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.1) 0%, transparent 70%), radial-gradient(circle at 70% 60%, rgba(255, 255, 255, 0.08) 0%, transparent 70%)'
                        }}
                    />

                    <div className="relative z-10 h-full flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeSlide}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.4 }}
                                className="text-center max-w-md mx-auto"
                            >
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.2, duration: 0.5 }}
                                    className="mb-6 flex justify-center"
                                >
                                    <div className="rounded-full bg-white/10 p-6 backdrop-blur-sm shadow-xl inline-flex">
                                        {sliderData[activeSlide].icon}
                                    </div>
                                </motion.div>

                                <motion.h2
                                    initial={{ y: 10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.3, duration: 0.4 }}
                                    className="text-3xl font-bold text-gray-800 mb-4"
                                >
                                    {sliderData[activeSlide].title}
                                </motion.h2>

                                <motion.p
                                    initial={{ y: 10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.4, duration: 0.4 }}
                                    className="text-gray-600 mb-3"
                                >
                                    {sliderData[activeSlide].description}
                                </motion.p>

                                <motion.p
                                    initial={{ y: 10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.5, duration: 0.4 }}
                                    className="text-gray-500 text-sm"
                                >
                                    {sliderData[activeSlide].subDescription}
                                </motion.p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-2">
                        {sliderData.map((_, idx) => (
                            <motion.button
                                key={idx}
                                onClick={() => setActiveSlide(idx)}
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                className={`w-2.5 h-2.5 rounded-full ${activeSlide === idx ? 'bg-secondary' : 'bg-gray-300'}`}
                            />
                        ))}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default AdminSignin;